import { createApi } from "@reduxjs/toolkit/dist/query/react"
import {
  Channel,
  FindChannelsParams,
  GetTransactionsParams,
  GetStatsParams,
  GetAutoReloadSettingsResponse,
  UpdateAutoReloadSettingsResponse,
  RetryBillingOrgResponse,
  UnpaidLeadsData
} from "../../types"
import { RTK_SLICE_API_LIST } from "../../common/constants"
import { baseQuery, getTags } from "../api"
import bbApi from "../../api"

const reducerPath = "channelLeads"

export const channelLeadsApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [
    "channels",
    "channel-balance",
    "channel-transactions",
    "channel-stats",
    "channel-unpaid-leads",
    "channel-auto-reload"
  ],
  endpoints: builder => ({
    // Channels
    findChannels: builder.query({
      queryFn: async (params: FindChannelsParams) => {
        const response = await bbApi.channelLeads.findChannels(params)
        return { data: response.channels ?? [] }
      },
      providesTags: result => getTags("channels", "id", result)
    }),

    createOrUpdateChannel: builder.mutation({
      queryFn: async (payload: Partial<Channel> & { org_id: string }) => {
        // Remove "id" field if it's empty which means it's CREATE action
        if (!payload.id) {
          delete payload.id
        }
        const data = await bbApi.channelLeads.createOrUpdateChannel(payload)
        return { data }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "channels", id: arg.id },
        { type: "channels", id: RTK_SLICE_API_LIST },
        { type: "channel-stats", id: RTK_SLICE_API_LIST }
      ]
    }),

    deleteChannel: builder.mutation({
      queryFn: async (id: string) => {
        const data = await bbApi.channelLeads.deleteChannel(id)
        return { data }
      },
      invalidatesTags: (result, error, id) => [
        { type: "channels", id },
        { type: "channels", id: RTK_SLICE_API_LIST },
        { type: "channel-stats", id: RTK_SLICE_API_LIST }
      ]
    }),

    // Balance
    getChannelBalance: builder.query({
      queryFn: async (org_id: string) => {
        const data = await bbApi.channelLeads.getBalance(org_id)
        return { data }
      },
      providesTags: () => [{ type: "channel-balance", id: RTK_SLICE_API_LIST }]
    }),

    addFunds: builder.mutation({
      queryFn: async (payload: {
        org_id: string
        amount_cents: number
        description?: string
      }) => {
        const data = await bbApi.channelLeads.addFunds(payload)
        return { data }
      },
      invalidatesTags: () => [
        { type: "channel-balance", id: RTK_SLICE_API_LIST },
        { type: "channel-transactions", id: RTK_SLICE_API_LIST }
      ]
    }),

    // Auto-Reload (org-wide)
    getChannelAutoReload: builder.query<GetAutoReloadSettingsResponse, string>({
      queryFn: async (org_id: string) => {
        const data = await bbApi.channelLeads.getAutoReloadSettings(org_id)
        return { data }
      },
      providesTags: () => [
        { type: "channel-auto-reload", id: RTK_SLICE_API_LIST }
      ]
    }),

    updateChannelAutoReload: builder.mutation<
      UpdateAutoReloadSettingsResponse,
      {
        org_id: string
        auto_reload_enabled?: boolean
        min_balance_cents?: number
        reload_amount_cents?: number
      }
    >({
      queryFn: async payload => {
        const data = await bbApi.channelLeads.updateAutoReloadSettings(payload)
        return { data }
      },
      invalidatesTags: () => [
        { type: "channel-auto-reload", id: RTK_SLICE_API_LIST },
        { type: "channel-balance", id: RTK_SLICE_API_LIST },
        { type: "channel-transactions", id: RTK_SLICE_API_LIST }
      ]
    }),

    // Transactions
    getChannelTransactions: builder.query({
      queryFn: async (params: GetTransactionsParams) => {
        const response = await bbApi.channelLeads.getTransactions(params)
        return { data: response.transactions ?? [] }
      },
      providesTags: result => {
        if (result) {
          return [
            ...getTags("channel-transactions", "id", result),
            { type: "channel-transactions", id: RTK_SLICE_API_LIST }
          ]
        }
        return [{ type: "channel-transactions", id: RTK_SLICE_API_LIST }]
      }
    }),

    // Stats
    getChannelStats: builder.query({
      queryFn: async (params: GetStatsParams) => {
        const data = await bbApi.channelLeads.getStats(params)
        return { data }
      },
      providesTags: () => [{ type: "channel-stats", id: RTK_SLICE_API_LIST }]
    }),

    // Unpaid Leads
    getUnpaidLeads: builder.query<UnpaidLeadsData, string>({
      queryFn: async (org_id: string) => {
        const response = await bbApi.channelLeads.getUnpaidLeads(org_id)
        return {
          data: {
            leads: response.unpaid_leads ?? [],
            // Org-wide total; the array is capped at 100 so it can be smaller
            count: response.unpaid_count ?? response.unpaid_leads?.length ?? 0
          }
        }
      },
      providesTags: result => {
        if (result) {
          return [
            ...getTags("channel-unpaid-leads", "id", result.leads),
            { type: "channel-unpaid-leads", id: RTK_SLICE_API_LIST }
          ]
        }
        return [{ type: "channel-unpaid-leads", id: RTK_SLICE_API_LIST }]
      }
    }),

    retryBilling: builder.mutation({
      queryFn: async (lead_ids: string[]) => {
        const data = await bbApi.channelLeads.retryBilling(lead_ids)
        return { data }
      },
      invalidatesTags: () => [
        { type: "channel-unpaid-leads", id: RTK_SLICE_API_LIST },
        { type: "channel-balance", id: RTK_SLICE_API_LIST },
        { type: "channel-transactions", id: RTK_SLICE_API_LIST },
        { type: "channel-stats", id: RTK_SLICE_API_LIST }
      ]
    }),

    // Org-wide retry — rebills the whole backlog in one call
    retryBillingOrg: builder.mutation<RetryBillingOrgResponse, string>({
      queryFn: async (org_id: string) => {
        const data = await bbApi.channelLeads.retryBillingOrg(org_id)
        return { data }
      },
      invalidatesTags: () => [
        { type: "channel-unpaid-leads", id: RTK_SLICE_API_LIST },
        { type: "channel-balance", id: RTK_SLICE_API_LIST },
        { type: "channel-transactions", id: RTK_SLICE_API_LIST },
        { type: "channel-stats", id: RTK_SLICE_API_LIST }
      ]
    })
  })
})

export const {
  useFindChannelsQuery,
  useCreateOrUpdateChannelMutation,
  useDeleteChannelMutation,
  useGetChannelBalanceQuery,
  useAddFundsMutation,
  useGetChannelAutoReloadQuery,
  useUpdateChannelAutoReloadMutation,
  useGetChannelTransactionsQuery,
  useGetChannelStatsQuery,
  useGetUnpaidLeadsQuery,
  useRetryBillingMutation,
  useRetryBillingOrgMutation
} = channelLeadsApi
